import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { showToast } from '@/components/layout/Toast'
import type { Cotizacion, Empresa, CotizacionItemsEstandar, CotizacionItemsEquipo, EquipoMiembro } from '@/lib/types'
import { formatCurrency, formatDate, getDiscountTier, processContent } from '@/lib/utils'

function fmtFTE(m: EquipoMiembro): string {
  if (m.dedicacion_fte === 1.0) return 'Full-Time'
  if (m.dedicacion_fte === 0.5) return 'Part-Time'
  if (m.dedicacion_fte === 0.25) return 'Quarter-Time'
  return `${m.horas_custom}h/mes`
}
function marginClass(mg: number): string {
  if (mg >= 50) return 'margin-excellent'
  if (mg >= 35) return 'margin-good'
  if (mg >= 20) return 'margin-warning'
  return 'margin-danger'
}

export default function VerCotizacion() {
  const { id } = useParams<{ id: string }>()
  const nav = useNavigate()
  const db = useSupabase()
  const pdfRef = useRef<HTMLDivElement>(null)

  const [cot, setCot] = useState<Cotizacion | null>(null)
  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  const [viewMode, setViewMode] = useState<'internal' | 'cliente'>('internal')

  useEffect(() => {
    (async () => {
      try {
        const [emp, c] = await Promise.all([
          db.getAll<Empresa>('empresa'),
          db.getById<Cotizacion>('cotizaciones', parseInt(id!)),
        ])
        setEmpresa(emp[0] || null); setCot(c)
        if (!c) { showToast('Cotización no encontrada', 'error'); nav('/cotizaciones') }
      } catch { showToast('Error al cargar', 'error') }
      finally { setLoading(false) }
    })()
  }, [id])

  const exportPDF = async () => {
    if (!pdfRef.current || !cot) return
    setExporting(true); showToast('Generando PDF…', 'info')
    try {
      const html2canvas = (await import('html2canvas')).default
      const { jsPDF } = await import('jspdf')

      const pages = pdfRef.current.querySelectorAll('.cotizacion-preview')
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
      const pdfW = 210; const pdfH = 297

      for (let i = 0; i < pages.length; i++) {
        if (i > 0) pdf.addPage()
        const canvas = await html2canvas(pages[i] as HTMLElement, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false })
        const ratio = canvas.width / canvas.height
        const imgW = pdfW; const imgH = imgW / ratio
        const finalH = Math.min(imgH, pdfH)
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, imgW, finalH)
      }
      const filename = `${cot.proyecto_nombre || 'Cotizacion'} - ${cot.cliente_nombre || cot.numero}.pdf`
      pdf.save(filename)
      showToast('PDF descargado ✓', 'success')
    } catch (e) { showToast('Error al exportar: ' + (e instanceof Error ? e.message : 'desconocido'), 'error') }
    finally { setExporting(false) }
  }

  if (loading || !cot || !empresa) return <div className="page-enter" style={{ padding: '2rem' }}><div className="page-header"><h1>Cotización</h1></div><div style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: 40 }}><div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...</div></div>

  const isEquipo = cot.tipo_cotizacion === 'equipo_dedicado'
  const items = (!isEquipo && Array.isArray(cot.items) ? cot.items : []) as CotizacionItemsEstandar[]
  const secciones = Array.isArray(cot.secciones) ? cot.secciones : []
  const proyectoNombre = cot.proyecto_nombre || items[0]?.descripcion || 'Propuesta de Servicios'
  const pc = empresa.color_primario || '#1C4587'
  const tier = getDiscountTier(cot.descuento_porcentaje)

  // Equipo data
  const equipoData = isEquipo && cot.items && typeof cot.items === 'object' && 'equipo' in cot.items ? cot.items as CotizacionItemsEquipo : null
  const equipoMembers = equipoData?.equipo ?? []
  const equipoTotales = equipoData?.totales
  const editPath = isEquipo ? `/equipo/${cot.id}/editar` : `/cotizaciones/${cot.id}/editar`

  return (
    <div className="page-enter">
      <div className="page-header">
        <div>
          <h1>Cotización #{cot.numero}{isEquipo ? ' — Equipo Dedicado' : ''}</h1>
          <p className="page-subtitle">{cot.cliente_nombre || 'Sin cliente'} — <span className={`badge badge-${cot.estado}`}>{cot.estado}</span></p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => nav('/cotizaciones')}>← Volver</button>
          <Link to={editPath} className="btn btn-secondary">Editar</Link>
          <button className="btn btn-primary" onClick={exportPDF} disabled={exporting}>
            {exporting ? '⏳ Exportando…' : '📥 Exportar PDF'}
          </button>
        </div>
      </div>

      {/* Equipo: dual view tabs */}
      {isEquipo && (
        <div className="eq-view-tabs">
          <button className={`eq-tab ${viewMode === 'internal' ? 'active' : ''}`} onClick={() => setViewMode('internal')}>📊 Vista Interna</button>
          <button className={`eq-tab ${viewMode === 'cliente' ? 'active' : ''}`} onClick={() => setViewMode('cliente')}>📄 Vista Cliente</button>
        </div>
      )}

      {/* Internal profitability dashboard (only for equipo) */}
      {isEquipo && viewMode === 'internal' && equipoData && (
        <div className="card internal-dashboard" style={{ marginBottom: 20 }}>
          <div className="card-header"><h3>📊 Tabla de Rentabilidad</h3><span className="badge badge-enviada" style={{ fontSize: '0.7rem' }}>Confidencial</span></div>
          <table className="eq-internal-tbl">
            <thead><tr><th>Rol</th><th>Cant.</th><th>Dedicación</th><th>Horas</th><th>Costo Int.</th><th>Precio Vta.</th><th>Margen</th></tr></thead>
            <tbody>
              {equipoMembers.map((m, i) => {
                const mg = m.precio_venta_linea > 0 ? ((m.precio_venta_linea - m.costo_total_linea) / m.precio_venta_linea * 100) : 0
                return <tr key={i}><td><strong>{m.rol_comercial}</strong> <span className={`seniority-badge seniority-${m.seniority.toLowerCase().replace('-','')}`}>{m.seniority}</span></td><td>{m.cantidad}</td><td>{fmtFTE(m)}</td><td>{m.horas_mensuales_linea}h</td><td style={{ color: 'var(--danger)' }}>${m.costo_total_linea.toFixed(2)}</td><td>${m.precio_venta_linea.toFixed(2)}</td><td><span className={`margin-indicator ${marginClass(mg)}`}>{mg.toFixed(1)}%</span></td></tr>
              })}
              <tr className="eq-internal-total"><td colSpan={3}><strong>TOTALES</strong></td><td><strong>{equipoTotales?.total_horas_equipo ?? 0}h</strong></td><td style={{ color: 'var(--danger)' }}><strong>${(equipoTotales?.costo_interno_total ?? 0).toFixed(2)}</strong></td><td><strong>${(equipoTotales?.precio_venta_sugerido ?? 0).toFixed(2)}</strong></td><td><span className={`margin-indicator ${marginClass(equipoTotales?.margen_global_proyectado ?? 0)}`} style={{ fontWeight: 700 }}>{(equipoTotales?.margen_global_proyectado ?? 0).toFixed(1)}%</span></td></tr>
            </tbody>
          </table>
          {equipoTotales && equipoTotales.precio_venta_final !== equipoTotales.precio_venta_sugerido && (
            <div style={{ marginTop: 12, padding: 10, background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.85rem' }}>
              <strong>Modificador Comercial:</strong> Sugerido ${equipoTotales.precio_venta_sugerido.toFixed(2)} → Final <strong>${equipoTotales.precio_venta_final.toFixed(2)}</strong>
            </div>
          )}
        </div>
      )}

      <div ref={pdfRef} id="cotizacion-pdf-content" style={isEquipo && viewMode === 'internal' ? { display: 'none' } : undefined}>
        {/* PAGE 1: COVER */}
        <div className="cotizacion-preview">
          <div className="cot-header" style={{ background: `linear-gradient(135deg, ${pc} 0%, ${pc}cc 100%)` }}>
            <div className="cot-header-left">
              {empresa.logo_url ? <img src={empresa.logo_url} className="cot-logo" alt={empresa.nombre ?? ''} /> : <div className="cot-logo-text">{empresa.nombre || '1Conecta'}</div>}
            </div>
            <div className="cot-header-right">
              <div className="cot-header-fiscal">{empresa.razon_social || ''}</div>
              <div>RUC {empresa.ruc || ''}</div>
            </div>
          </div>

          <div className="cot-title-banner" style={{ borderLeft: `4px solid ${pc}` }}>
            <div className="cot-title-label">{isEquipo ? 'PROPUESTA DE EQUIPO DEDICADO' : 'COTIZACIÓN'}</div>
            <div className="cot-title-amount">{isEquipo ? 'INVERSIÓN MENSUAL' : 'PRESUPUESTO'}: <strong>{formatCurrency(cot.total, cot.moneda)}</strong></div>
          </div>

          <div className="cot-meta">
            <div className="cot-meta-block">
              <h4>Cotizado a</h4>
              <div className="cot-meta-grid">
                <span className="cot-meta-key">E:</span><span>{cot.cliente_nombre || '—'}</span>
                <span className="cot-meta-key">C:</span><span>{cot.cliente_contacto || '—'}</span>
                <span className="cot-meta-key">T:</span><span>{cot.cliente_telefono || '—'}</span>
                <span className="cot-meta-key">@:</span><span>{cot.cliente_email || '—'}</span>
              </div>
            </div>
            <div className="cot-meta-block" style={{ textAlign: 'right' }}>
              <h4>Detalles</h4>
              <div className="cot-meta-grid" style={{ justifyItems: 'end' }}>
                <span className="cot-meta-key">N:</span><span>{cot.numero}</span>
                <span className="cot-meta-key">F:</span><span>{formatDate(cot.fecha_emision)}</span>
                <span className="cot-meta-key">D:</span><span>Validez: {cot.validez_dias} días calendario</span>
              </div>
            </div>
          </div>

          <div className="cot-body">
            <p className="cot-intro">
              {isEquipo
                ? <>A través del presente documento, ponemos a su disposición la propuesta de <strong>{proyectoNombre}</strong>. A continuación, se detalla la composición del equipo profesional dedicado y la inversión mensual asociada.</>
                : <>A través del presente documento, ponemos a su disposición la propuesta económica correspondiente al <strong>{proyectoNombre}</strong>. En la siguiente sección encontrará el desglose detallado de la inversión.</>
              }
            </p>

            {isEquipo ? (
              <>
                <h3 className="cot-section-title" style={{ borderColor: pc }}>Composición del Equipo</h3>
                <table className="cot-table">
                  <thead><tr><th style={{ width: '40%' }}>Rol Profesional</th><th style={{ width: '15%' }}>Cantidad</th><th style={{ width: '25%' }}>Dedicación</th><th style={{ width: '20%' }}>Horas / Mes</th></tr></thead>
                  <tbody>
                    {equipoMembers.map((m, i) => <tr key={i}><td><strong>{m.rol_comercial}</strong> ({m.seniority})</td><td>{m.cantidad}</td><td>{fmtFTE(m)}</td><td>{m.horas_mensuales_linea}h</td></tr>)}
                    <tr style={{ background: 'var(--bg-secondary)', fontWeight: 700 }}><td colSpan={3}>Total Horas del Equipo</td><td>{equipoTotales?.total_horas_equipo ?? 0}h</td></tr>
                  </tbody>
                </table>
              </>
            ) : (
              <table className="cot-table">
                <thead><tr><th style={{ width: '50%' }}>Descripción</th><th style={{ width: '18%' }}>Costo</th><th style={{ width: '10%' }}>Cant.</th><th style={{ width: '22%' }}>Total</th></tr></thead>
                <tbody>{items.map((it, i) => (
                  <tr key={i}><td>{it.descripcion}</td><td>{formatCurrency(it.precio_unitario, cot.moneda)}</td><td>{it.cantidad}</td><td>{formatCurrency(it.total || it.precio_unitario * it.cantidad, cot.moneda)}</td></tr>
                ))}</tbody>
              </table>
            )}

            <div className="cot-bottom-row">
              <div className="cot-payment-box">
                <h4>Datos para Transferencia Bancaria</h4>
                <div className="cot-bank-info">
                  <div className="cot-bank-section"><div className="cot-bank-label">Banco</div><div className="cot-bank-value">{empresa.banco || 'Interbank'}</div></div>
                  <div className="cot-bank-section"><div className="cot-bank-label">Beneficiario</div><div className="cot-bank-value">{empresa.razon_social || ''}</div></div>
                  <div className="cot-bank-section"><div className="cot-bank-label">RUC</div><div className="cot-bank-value">{empresa.ruc || ''}</div></div>
                  <div className="cot-bank-divider" />
                  <div className="cot-bank-section"><div className="cot-bank-sublabel">Depósito Nacional (USD)</div><div className="cot-bank-label">Cta. Corriente Dólares</div><div className="cot-bank-value cot-bank-mono">{empresa.cuenta_corriente || ''}</div></div>
                  <div className="cot-bank-divider" />
                  <div className="cot-bank-section"><div className="cot-bank-sublabel">Transferencia Interbancaria (CCI)</div><div className="cot-bank-label">Código CCI (Dólares)</div><div className="cot-bank-value cot-bank-mono">{empresa.cci || ''}</div></div>
                  <div className="cot-bank-divider" />
                  <div className="cot-bank-section"><div className="cot-bank-sublabel">Transferencia Internacional (SWIFT)</div><div className="cot-bank-label">Código SWIFT/BIC</div><div className="cot-bank-value cot-bank-mono">{empresa.swift || ''}</div></div>
                </div>
              </div>
              <div className="cot-totals-box">
                <div className="cot-totals-row"><span>Subtotal</span><span>{formatCurrency(cot.subtotal, cot.moneda)}</span></div>
                <div className="cot-totals-row"><span>IGV ({cot.igv_porcentaje}%)</span><span>{formatCurrency(cot.igv_monto, cot.moneda)}</span></div>
                {tier.label !== 'Sin descuento' ? (
                  <div className="cot-totals-row cot-discount-row">
                    <span className="cot-discount-label"><span className="cot-discount-badge" style={{ background: tier.color }}>{tier.emoji} {tier.label}</span><span className="cot-discount-pct">-{cot.descuento_porcentaje}%</span></span>
                    <span>-{formatCurrency(cot.descuento_monto, cot.moneda)}</span>
                  </div>
                ) : (
                  <div className="cot-totals-row"><span>Descuento 0%</span><span>{formatCurrency(0, cot.moneda)}</span></div>
                )}
                <div className="cot-totals-row total" style={{ background: pc }}><span>TOTAL GENERAL</span><span>{formatCurrency(cot.total, cot.moneda)}</span></div>
              </div>
            </div>

            {secciones.length > 0 && (
              <p className="cot-note">La tabla anterior representa un resumen económico. Para conocer las especificaciones técnicas, cronogramas, entregables y el desglose detallado, <strong>le invitamos a revisar las páginas siguientes</strong>.</p>
            )}
          </div>

          <div className="cot-footer" style={{ background: pc }}>
            <span>{empresa.nombre || '1Conecta'}</span>
            <span>{empresa.ubicacion || ''} | {empresa.telefono || ''}</span>
            <span>{empresa.website || ''}</span>
          </div>
        </div>

        {/* PAGE 2: INDEX (if sections exist) */}
        {secciones.length > 0 && (
          <div className="cotizacion-preview cot-page" style={{ marginTop: 24 }}>
            <div className="cot-page-header" style={{ background: pc }} />
            <div className="cot-page-ruc">{cot.cliente_ruc || ''}</div>
            <div className="cot-body" style={{ paddingTop: 40 }}>
              <h2 className="cot-page-title">Cotización de Servicios</h2>
              <div className="cot-index-meta">
                <p>Fecha de Emisión: <em>{formatDate(cot.fecha_emision)}</em></p>
                <p>Proyecto/Servicio: <em>{proyectoNombre}</em></p>
              </div>
              <div className="cot-index-box">
                <h3>Índice</h3>
                {secciones.map((s, i) => (
                  <div key={i} className="cot-index-item"><strong>{s.titulo}</strong><span className="cot-index-dots" /><span className="cot-index-page">{i + 3}</span></div>
                ))}
              </div>
            </div>
            <div className="cot-footer" style={{ background: pc }}><span>{empresa.nombre}</span><span>Pág. 2</span><span>{empresa.website}</span></div>
          </div>
        )}

        {/* SECTION PAGES */}
        {secciones.map((sec, idx) => (
          <div key={idx} className="cotizacion-preview cot-page" style={{ marginTop: 24 }}>
            <div className="cot-page-header" style={{ background: pc }} />
            <div className="cot-page-ruc">{cot.cliente_ruc || ''}</div>
            <div className="cot-body">
              <h3 className="cot-section-title" style={{ borderColor: pc }}>{sec.titulo}</h3>
              <div className="cot-section-content" dangerouslySetInnerHTML={{ __html: processContent(sec.contenido) }} />
            </div>
            <div className="cot-footer" style={{ background: pc }}><span>{empresa.nombre}</span><span>Pág. {idx + 3}</span><span>{empresa.website}</span></div>
          </div>
        ))}
      </div>
    </div>
  )
}
